/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AiOutlinePushpin } from "react-icons/ai";
import { getListCover } from "../../../utils/projectImages";
import SortDropdown from "../../../Components/SortDropdown";
import { useSortedItems } from "../../../Hooks/useSortedItems";
import { usePersistedPagination } from "../../../Hooks/usePersistedPagination";

gsap.registerPlugin(ScrollTrigger);

const ITEMS_PER_PAGE = 8;

const cuisinesData = [
  { id: 1,  title: "Project 01 - île de France , France",  link: "project1" },
  { id: 2,  title: "Project 02 - Nice, France",            link: "project2" },
  { id: 3,  title: "Project 03 - île de France , France",  link: "project3" },
  { id: 4,  title: "Project 04 - île de France , France",  link: "project4" },
  { id: 5,  title: "Project 05 - île de France , France",  link: "project5" },
  { id: 6,  title: "Project 06 - île de France , France",  link: "project6" },
  { id: 7,  title: "Project 07 - île de France , France",  link: "project7" },
  { id: 8,  title: "Project 08 - île de France , France",  link: "project8" },
  { id: 9,  title: "Project 09 - île de France , France",  link: "project9" },
  { id: 10, title: "Project 10  - île de France , France", link: "project10" },
  { id: 11, title: "Project 11 - île de France , France",  link: "project11" },
  { id: 12, title: "Project 12 - île de France , France",  link: "project12" },
].map((item) => ({
  ...item,
  // Image lue dans le dossier du projet : <projet>/<projet>-bottom.jpg
  pic: getListCover("international", item.link),
}));

export default function Projets({ navigateWithTransition }) {
  const [currentPage, setCurrentPage, sortOrder, setSortOrder] =
    usePersistedPagination("projets-international");
  const sortedData = useSortedItems(cuisinesData, sortOrder);
  const contentRef = useRef(null);
  const topRef = useRef(null);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleItems = sortedData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Si la page sauvegardée n'existe plus (moins de projets), on revient à la page 1
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    // Petite pause pour laisser le DOM se mettre en place après le changement de page
    const timeout = setTimeout(() => {
      const ctx = gsap.context(() => {
        const text = container.querySelector(".Cuisine_text");
        const figures = container.querySelectorAll(".Cuisine_figure");

        // ── Texte intro ──────────────────────────────────────────
        gsap.fromTo(
          text,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            delay: currentPage === 1 ? 0.3 : 0,
            scrollTrigger: {
              trigger: text,
              start: "top 88%",
              once: true,
            },
          }
        );

        // ── Figures avec stagger ─────────────────────────────────
        figures.forEach((fig, i) => {
          gsap.fromTo(
            fig,
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power2.out",
              delay: i * 0.12,
              scrollTrigger: {
                trigger: fig,
                start: "top 88%",
                once: true,
              },
            }
          );
        });
      }, container);

      return () => ctx.revert();
    }, 50);

    return () => clearTimeout(timeout);
  }, [currentPage, sortOrder]);

  const handleSortChange = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="Cuisines Cuisines--sortable">
      <div ref={topRef} />

      <div ref={contentRef} className="Cuisines_content">
        <div className="Cuisine_text">
          <h2>Nos projets à travers le monde</h2>
          <p>
            Sans frontières, Elements donne vie à vos projets partout dans le
            monde. De la conception à la livraison, jusqu'au service
            après-vente, nous vous offrons un accompagnement sur mesure, avec
            une exigence constante d'excellence.
          </p>
        </div>

        {visibleItems.map((item, index) => (
          <div className="Cuisine_figure" key={item.id}>
            {index === 0 && (
              <div className="Cuisine_sort">
                <SortDropdown value={sortOrder} onChange={handleSortChange} />
              </div>
            )}
            <a href={`/export/projets-international/${item.link}`}>
              <figure>
                <img src={item.pic} alt={item.title} />
                <figcaption>
                  <AiOutlinePushpin size={16} />
                  <span>{item.title}</span>
                </figcaption>
              </figure>
            </a>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="Cuisines_pagination">
          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Page précédente"
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`Pagination_btn ${currentPage === page ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          ))}

          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}